/**
 * Reading `Dashboard.progression`, honestly.
 *
 * `core-api` types this field as an open object and returns `null` until
 * `mastery` is wired — deliberately: "a dashboard that invented a level would
 * be believed" (`schemas.py`). So this module does two things and no more:
 * it parses the shape `mastery.dashboard_snapshot` produces, and it returns
 * `null` for anything it does not recognise. Nothing here fills a gap with a
 * plausible number.
 *
 * When `mastery` lands, this file is the single place to adjust.
 */

import { type Penurie } from "@/ui/penurie";
import { isMasteryState, MASTERY_ORDER, type MasteryState } from "@/config/wording";

export type CompetencyReading = {
  competencyId: string;
  label: string;
  subject: string;
  state: MasteryState;
  /** 0–100, where 50 is "at the programme's level on this date". */
  score: number;
  expected: number;
  observations: number;
  due: boolean;
};

export type SubjectReading = {
  subject: string;
  label: string;
  score: number;
  expected: number;
  observations: number;
  /** Le poids de la matière dans le niveau d'ensemble. 1 quand rien n'est déclaré. */
  coefficient: number;
  coefficientSource: "ecoledirecte" | "defaut";
};

/**
 * Le niveau actuel, en une position.
 *
 * `score` est sur la même échelle que partout ailleurs — 50 est le niveau
 * attendu du programme à cette date. Les quatre champs qui l'accompagnent ne
 * sont pas décoratifs : une position tirée d'une matière sur neuf, ou d'une
 * pondération à poids égal faute de coefficients importés, ne se lit pas comme
 * une position tirée de tout le bulletin. L'écran le dit ; il ne le devine pas.
 */
export type Overall = {
  score: number;
  expected: number;
  observations: number;
  subjectsMeasured: number;
  subjectsTotal: number;
  /** `uniforme` : aucun coefficient importé — un aveu, pas une panne. */
  weighting: "ecoledirecte" | "uniforme";
  weightedSubjects: number;
};

/** Une compétence vue en classe et jamais mesurée ici. */
export type TaughtCompetency = {
  competencyId: string;
  label: string;
  subject: string;
  /** Pourquoi on la croit traitée. Jamais un mot écrit par l'enfant. */
  reason: string;
  /**
   * Ce qui empêche de lui proposer un exercice — ou `null` : du stock est à
   * portée, et il n'y a rien à faire.
   *
   * Ajouté le 8 septembre 2026 sur la remarque de Hedi : *« je ne vois pas
   * d'exercices correspondant à ces compétences »*. La carte listait un manque
   * sans en donner la cause, et les quatre causes appellent des gestes
   * **opposés** — générer, régler l'échelle, attendre une date, ou rien.
   */
  shortage: Penurie | null;
};

/**
 * Une compétence que le découpage de l'année place à cette période, et qu'aucune
 * source n'a confirmée.
 *
 * **Sans `shortage`, et l'absence est le propos.** « Pourquoi aucun exercice ne
 * lui est proposé » est une question qui ne se pose pas encore sur un chapitre
 * dont on ignore s'il a eu lieu ; le serveur n'en calcule donc aucune cause, et
 * un `null` fabriqué ici se lirait comme « rien ne l'empêche », c'est-à-dire une
 * réponse là où il n'y a pas de question. C'est aussi pourquoi ce type est
 * distinct de `TaughtCompetency` plutôt qu'une réutilisation à champ vide.
 */
export type ProbableCompetency = {
  competencyId: string;
  label: string;
  subject: string;
  /** Pourquoi on la croit traitée — et, dans la même phrase, ce qui manque. */
  reason: string;
};

/**
 * Le trou entre l'école et l'application.
 *
 * `total` accompagne `unmeasured` parce qu'une liste vide a deux causes
 * opposées : tout ce que la classe a traité a été mesuré, ou rien n'a encore
 * été rattaché. Un écran qui ne saurait pas les distinguer dirait la première
 * en pensant la seconde. `pendingReadings` en ajoute une troisième, la seule
 * qui passe toute seule : la lecture de la journée est encore en vol.
 */
export type Taught = {
  total: number;
  unmeasured: TaughtCompetency[];
  /**
   * Les journées dont la lecture n'a pas abouti — la **troisième** cause d'une
   * liste vide, et la seule qui soit temporaire.
   */
  pendingReadings: number;
  /**
   * Celles que le programme place à cette période sans que rien ne les nomme.
   *
   * Une **seconde liste** et non une colonne de la première : `unmeasured` dit
   * « la classe l'a traité », ce dont une source répond, et celle-ci dit « elle
   * l'a probablement traité », ce dont personne ne répond. Les fondre en une
   * liste triée ferait lire la seconde avec l'assurance de la première, et c'est
   * précisément la distinction que le parent vient trancher.
   */
  probables: ProbableCompetency[];
};

export type Progression = {
  subjects: SubjectReading[];
  competencies: CompetencyReading[];
  /** `null` tant que le back-end ne le sert pas — jamais recalculé ici. */
  overall: Overall | null;
  taught: Taught | null;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const asString = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.length > 0 ? value : fallback;

function readCompetency(raw: unknown): CompetencyReading | null {
  const record = asRecord(raw);
  if (!record) return null;
  const competencyId = asString(record.competency_id, "");
  const state = record.state;
  if (!competencyId || !isMasteryState(state)) return null;
  return {
    competencyId,
    label: asString(record.label, competencyId),
    subject: asString(record.subject, "autre"),
    state,
    score: asNumber(record.score, 50),
    expected: asNumber(record.expected, 50),
    observations: asNumber(record.observations, 0),
    due: record.due === true,
  };
}

function readSubject(raw: unknown): SubjectReading | null {
  const record = asRecord(raw);
  if (!record) return null;
  const subject = asString(record.subject ?? record.id, "");
  if (!subject) return null;
  const coefficient = asNumber(record.coefficient, 1);
  return {
    subject,
    label: asString(record.label, subject),
    score: asNumber(record.score, 50),
    expected: asNumber(record.expected, 50),
    observations: asNumber(record.observations, 0),
    coefficient: coefficient > 0 ? coefficient : 1,
    coefficientSource: record.coefficient_source === "ecoledirecte" ? "ecoledirecte" : "defaut",
  };
}

/**
 * Le niveau d'ensemble, lu et non reconstitué.
 *
 * Rendre `null` plutôt qu'une moyenne des jauges quand le champ manque : une
 * moyenne calculée ici serait à poids égal et se présenterait avec le même
 * aplomb que la pondération de l'établissement, ce qui est exactement la
 * confusion que `weighting` existe pour éviter.
 */
function readOverall(raw: unknown): Overall | null {
  const record = asRecord(raw);
  if (!record) return null;
  return {
    score: asNumber(record.score, 50),
    expected: asNumber(record.expected, 50),
    observations: asNumber(record.observations, 0),
    subjectsMeasured: asNumber(record.subjects_measured, 0),
    subjectsTotal: asNumber(record.subjects_total, 0),
    weighting: record.weighting === "ecoledirecte" ? "ecoledirecte" : "uniforme",
    weightedSubjects: asNumber(record.weighted_subjects, 0),
  };
}

function readTaughtCompetency(raw: unknown): TaughtCompetency | null {
  const record = asRecord(raw);
  if (!record) return null;
  const competencyId = asString(record.competency_id, "");
  if (!competencyId) return null;
  return {
    competencyId,
    label: asString(record.label, competencyId),
    subject: asString(record.subject, "autre"),
    reason: asString(record.reason, ""),
    shortage: readPenurie(record.shortage),
  };
}

/**
 * La pénurie, ou `null`.
 *
 * `null` couvre deux choses que l'écran dit pareil, et c'est voulu : du stock à
 * portée, et un diagnostic qui n'a pas abouti. Le serveur renvoie `null` dans
 * les deux cas — il refuse d'emporter le tableau de bord pour une colonne
 * secondaire —, et inventer ici une troisième phrase reviendrait à prétendre
 * savoir laquelle des deux on lit.
 */
function readPenurie(raw: unknown): Penurie | null {
  const record = asRecord(raw);
  if (!record) return null;
  const cause = asString(record.cause, "");
  if (!cause) return null;
  return {
    cause,
    formes_attendues: Array.isArray(record.formes_attendues)
      ? record.formes_attendues.filter((forme): forme is string => typeof forme === "string")
      : [],
    redisponible_le: typeof record.redisponible_le === "string" ? record.redisponible_le : null,
  };
}

function readProbable(raw: unknown): ProbableCompetency | null {
  const record = asRecord(raw);
  if (!record) return null;
  const competencyId = asString(record.competency_id, "");
  if (!competencyId) return null;
  return {
    competencyId,
    label: asString(record.label, competencyId),
    subject: asString(record.subject, "autre"),
    reason: asString(record.reason, ""),
  };
}

function readTaught(raw: unknown): Taught | null {
  const record = asRecord(raw);
  if (!record) return null;
  const unmeasured = Array.isArray(record.unmeasured)
    ? record.unmeasured
        .map(readTaughtCompetency)
        .filter((entry): entry is TaughtCompetency => entry !== null)
    : [];
  const probables = Array.isArray(record.probables)
    ? record.probables
        .map(readProbable)
        .filter((entry): entry is ProbableCompetency => entry !== null)
    : [];
  // Le total est ce qui rend la liste vide lisible : à défaut, on ne prétend pas
  // en savoir plus que le nombre de lignes effectivement reçues. Zéro pour les
  // lectures en attente, par la même prudence : un back-end qui ne le sert pas
  // ramène l'écran aux deux états qu'il affichait avant — et une liste de
  // probables absente le ramène à la carte d'avant le 15 septembre 2026, qui
  // n'en montrait aucune.
  return {
    total: asNumber(record.total, unmeasured.length),
    unmeasured,
    pendingReadings: Math.max(0, Math.trunc(asNumber(record.pending_readings, 0))),
    probables,
  };
}

export function readProgression(raw: unknown): Progression | null {
  const record = asRecord(raw);
  if (!record) return null;

  const competencies = Array.isArray(record.competencies)
    ? record.competencies.map(readCompetency).filter((entry): entry is CompetencyReading => entry !== null)
    : [];

  const subjects = Array.isArray(record.subjects)
    ? record.subjects.map(readSubject).filter((entry): entry is SubjectReading => entry !== null)
    : summariseBySubject(competencies);

  if (subjects.length === 0 && competencies.length === 0) return null;
  return {
    subjects,
    competencies,
    overall: readOverall(record.overall),
    taught: readTaught(record.taught),
  };
}

/** A subject line is the mean of its competencies — stated, never invented. */
function summariseBySubject(competencies: CompetencyReading[]): SubjectReading[] {
  const grouped = new Map<string, CompetencyReading[]>();
  for (const competency of competencies) {
    const bucket = grouped.get(competency.subject) ?? [];
    bucket.push(competency);
    grouped.set(competency.subject, bucket);
  }
  return [...grouped.entries()].map(([subject, entries]) => ({
    subject,
    label: subject,
    score: mean(entries.map((entry) => entry.score)),
    expected: mean(entries.map((entry) => entry.expected)),
    observations: entries.reduce((total, entry) => total + entry.observations, 0),
    coefficient: 1,
    coefficientSource: "defaut" as const,
  }));
}

const mean = (values: number[]): number =>
  values.length === 0 ? 50 : values.reduce((total, value) => total + value, 0) / values.length;

export const fragile = (progression: Progression): CompetencyReading[] =>
  progression.competencies.filter((entry) => entry.state === "fragile");

export const ahead = (progression: Progression): CompetencyReading[] =>
  progression.competencies.filter((entry) => entry.score - entry.expected > 10);

/* --- Les filtres de la carte complète (web-pwa §6.3) ----------------------- */

/**
 * **Pourquoi la carte complète a besoin de filtres.**
 *
 * Elle n'est pas un extrait : `progression_for` énumère *toutes* les compétences
 * du programme de l'enfant, observées ou non — 114 pour Baya, 107 pour Mehdi, et
 * ce nombre ne baissera jamais. Une liste de cette longueur ne se parcourt pas.
 * Sans filtre, la carte complète est complète et illisible, ce qui la rend
 * ornementale : le parent lit les deux cartes du dessus et ne l'ouvre plus.
 *
 * **Pourquoi « évaluée » se lit sur l'état et non sur le nombre d'observations.**
 * `mastery.state` rend `non_evalue` tant qu'une compétence n'a pas *assez*
 * d'observations pour être jugée (§13.4) — deux tentatives ne suffisent pas — et
 * c'est ce mot-là que la pastille affiche à côté du libellé. Filtrer sur
 * `observations > 0` ferait donc apparaître sous « évaluées » des lignes que la
 * pastille d'à côté déclare non évaluées, sur la même ligne de la même liste.
 * Une seule définition, et c'est celle qui est écrite à l'écran.
 */
export type Mesure = "toutes" | "evaluees" | "non-evaluees";

export type FiltreCompetences = {
  /** `null` : toutes les matières. */
  subject: string | null;
  /**
   * `null` : tous les états. **Jamais `non_evalue`** — cet état-là est porté par
   * `mesure`, et l'offrir aux deux endroits laisserait composer « non évaluées »
   * avec « fragile », c'est-à-dire une liste vide par construction.
   */
  state: MasteryState | null;
  mesure: Mesure;
};

export const SANS_FILTRE: FiltreCompetences = { subject: null, state: null, mesure: "toutes" };

/** Les états qu'on peut choisir : ceux d'une compétence effectivement mesurée. */
export const ETATS_EVALUES: readonly MasteryState[] = MASTERY_ORDER.filter(
  (state) => state !== "non_evalue",
);

export const estEvaluee = (entry: CompetencyReading): boolean => entry.state !== "non_evalue";

export const filtreActif = (filtre: FiltreCompetences): boolean =>
  filtre.subject !== null || filtre.state !== null || filtre.mesure !== "toutes";

export function filtrerCompetences(
  entries: CompetencyReading[],
  filtre: FiltreCompetences,
): CompetencyReading[] {
  return entries.filter((entry) => {
    if (filtre.subject !== null && entry.subject !== filtre.subject) return false;
    if (filtre.mesure === "evaluees" && !estEvaluee(entry)) return false;
    if (filtre.mesure === "non-evaluees" && estEvaluee(entry)) return false;
    if (filtre.state !== null && entry.state !== filtre.state) return false;
    return true;
  });
}

/**
 * Ce que la liste dit d'elle-même sous les filtres.
 *
 * Le dénominateur est là pour une raison : sans lui, « 6 compétences » après un
 * filtre se lit comme « il y en a six », et le parent conclut que le programme
 * en compte six. Le total est ce qui rend le filtre visible.
 */
export function resumeDuFiltre(affichees: number, total: number): string {
  const accord = (n: number) => (n > 1 ? "compétences" : "compétence");
  if (affichees === total) return `${total} ${accord(total)}`;
  return `${affichees} ${accord(affichees)} sur ${total}`;
}

/* --- Ce que la carte « niveau actuel » écrit sous sa jauge ------------------ */

/**
 * La phrase qui empêche de surinterpréter la position.
 *
 * Deux réserves, et elles sont indépendantes : **sur quoi** la position est
 * calculée (les matières effectivement mesurées, souvent une sur neuf le
 * premier soir) et **comment** elles sont pesées (les coefficients de
 * l'établissement, ou à poids égal faute de les avoir importés). Taire la
 * seconde ferait passer une moyenne à poids égal pour un bulletin ; taire la
 * première ferait passer une matière pour l'ensemble du travail.
 */
export function resumeDuNiveau(overall: Overall): string {
  const accord = (n: number) => (n > 1 ? "matières mesurées" : "matière mesurée");
  const couverture = `${overall.subjectsMeasured} ${accord(overall.subjectsMeasured)} sur ${overall.subjectsTotal}`;
  const ponderation =
    overall.weighting === "ecoledirecte"
      ? "pondérées par les coefficients de l'établissement"
      : "à poids égal — aucun coefficient importé";
  return `${couverture}, ${ponderation}.`;
}

/**
 * Ce qu'il faut dire quand « Vu en classe, jamais mesuré ici » ne montre rien.
 *
 * Trois causes, dans cet ordre de priorité. **La lecture en vol passe devant**
 * parce qu'elle rend les deux autres indécidables : tant qu'une journée n'a pas
 * été lue, `total` ne compte pas ce qu'elle aurait désigné, et un `total` à zéro
 * ne prouve plus que rien n'est rattaché. Répondre « rien n'est rattaché » dans
 * cet état, c'est annoncer une panne à un parent dont le système fonctionne —
 * ce qui est arrivé le 6 septembre 2026 au soir, dix images ayant rouvert d'un
 * coup les neuf journées de la rentrée.
 *
 * **Elle ne parle que de la liste attestée.** Depuis le 15 septembre 2026 la
 * carte en porte une seconde, les probables, et une carte qui montre des lignes
 * ne peut pas afficher une phrase de vide : l'appelant la place en tête quand
 * seules les probables restent, et en lieu et place de la carte quand les deux
 * listes sont vides.
 */
export function phraseDuVide(taught: Taught): string {
  if (taught.pendingReadings > 0) {
    const journees =
      taught.pendingReadings > 1
        ? `${taught.pendingReadings} journées sont`
        : "Une journée est";
    return `${journees} en cours de lecture. Ce que la classe a traité s'affichera ici une fois la lecture terminée.`;
  }
  if (taught.total === 0) {
    return "Rien n'est encore rattaché à une compétence : ni le cahier de textes ni le journal du soir n'ont désigné de notion.";
  }
  return "Tout ce que la classe a traité a déjà été travaillé ici.";
}
